// src/hooks/useLoadBookingTools.jsx
import { useCallback, useState } from 'react'
import { negotiateRate } from '../api/mockApi'
import { tell } from '../api/auth'

export function useLoadBookingTools({ navigate, toast, loadData }) {
  // Rate negotiation state
  const [lowerRate, setLowerRate] = useState('')
  const [upperRate, setUpperRate] = useState('')
  
  // Reset rates based on load data
  const resetRates = useCallback(() => {
    if (!loadData) return
    
    // Pre-populate rates based on current rate if available
    const currentRate = loadData.rateUsd ?? loadData.estimatedRatePerMile
    if (currentRate) {
      // Set default lower rate to 5% below current
      setLowerRate((currentRate * 1.02).toFixed(2))
      localStorage.setItem('lowerRate', (currentRate * 1.02).toFixed(2))
      // Set default upper rate to 5% above current
      setUpperRate((currentRate * 1.20).toFixed(2))
      localStorage.setItem('upperRate', (currentRate * 1.20).toFixed(2))
    }
  }, [loadData])

  // Set lower rate tool (for AI to use)
  const setLowerRateTool = useCallback(({ rate }) => {
    console.log("rate",rate)
    localStorage.setItem('lowerRate', rate);
    const numRate = parseFloat(rate)
    if (isNaN(numRate) || numRate <= 0) {
      toast({
        title: "Invalid rate",
        description: "Please enter a positive number",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return { success: false, message: "Invalid rate" }
    }
    
    setLowerRate(numRate.toFixed(2))
    return { success: true, rate: numRate.toFixed(2) }
  }, [toast])

  // Set upper rate tool (for AI to use)
  const setUpperRateTool = useCallback(({ rate }) => {
    localStorage.setItem('upperRate', rate);
    const numRate = parseFloat(rate)
    if (isNaN(numRate) || numRate <= 0) {
      toast({
        title: "Invalid rate",
        description: "Please enter a positive number",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return { success: false, message: "Invalid rate" }
    }
    
    setUpperRate(numRate.toFixed(2))
    return { success: true, rate: numRate.toFixed(2) }
  }, [toast])

  // Negotiate rate tool (for AI to use)
  const negotiateRateTool = useCallback(async () => {
    let lower = parseFloat(lowerRate)
    let upper = parseFloat(upperRate)
    if (isNaN(lower) || isNaN(upper)) {
      lower = parseFloat(localStorage.getItem('lowerRate'))
      upper = parseFloat(localStorage.getItem('upperRate'))
    }
    const driverId = localStorage.getItem('driverId')
    console.log("lower",lower)
    console.log("upper",upper)
    // Validate rates
    if (isNaN(lower) || isNaN(upper) || lower <= 0 || upper <= lower) {
      toast({
        title: "Invalid rate range",
        description: "Upper rate must be higher than lower rate, and both must be positive",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return { success: false, message: "Invalid rate range" }
    }
    
    tell(driverId, "lower_bound_rate", `${lower}`)
    // wait 1 second
    await new Promise(resolve => setTimeout(resolve, 1000));
    tell(driverId, "upper_bound_rate", `${upper}`)

    
      // Here you'd typically send both the lower and upper rates

      toast({
        title: "Rate negotiation initiated",
        description: `Your rate range $${lowerRate}-$${upperRate}/mi has been submitted.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      })
      
      navigate('/confirm', { state: { lowerRate, upperRate } });
    
  }, [loadData, lowerRate, upperRate, toast, navigate])

  // Call broker tool (for AI to use)
  const callBrokerTool = useCallback(() => {
    if (!loadData || !loadData.brokerPhone) {
      toast({
        title: "No broker phone available",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return { success: false, message: "No broker phone available" }
    }
    
    // In a real app, this might use a calling API or native functionality
    window.location.href = `tel:${loadData.brokerPhone}`
    
    toast({
      title: "Calling broker",
      description: `Dialing ${loadData.brokerPhone}`,
      status: "info",
      duration: 3000,
      isClosable: true,
    })
    
    return { success: true, message: `Calling broker: ${loadData.brokerPhone}` }
  }, [loadData, toast])

  // Email broker tool (for AI to use)
  const emailBrokerTool = useCallback(() => {
    if (!loadData || !loadData.brokerEmail) {
      toast({
        title: "No broker email available",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return { success: false, message: "No broker email available" }
    }
    
    const lower = parseFloat(lowerRate)
    const upper = parseFloat(upperRate)
    
    // Validate rates
    if (isNaN(lower) || isNaN(upper) || lower <= 0 || upper <= lower) {
      toast({
        title: "Invalid rate range",
        description: "Please set valid rates before emailing",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return { success: false, message: "Invalid rate range" }
    }
    
    // Format an email with negotiation details
    const subject = `Load #${loadData.id} Rate Negotiation`
    const body = `Hello,\n\nI'm interested in Load #${loadData.id} and would like to negotiate a rate between $${lowerRate}/mi and $${upperRate}/mi.\n\nPlease contact me to discuss further.\n\nThank you.`
    
    window.location.href = `mailto:${loadData.brokerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    
    return { success: true, message: `Email prepared for: ${loadData.brokerEmail}` }
  }, [loadData, lowerRate, upperRate, toast])

  // Back to results tool (for AI to use)
  const backToResultsTool = useCallback(() => {
    navigate('/results')
    return { success: true, message: "Navigated back to results" }
  }, [navigate])

  return {
    // State
    lowerRate,
    upperRate,
    
    // Setters
    setLowerRate,
    setUpperRate,
    resetRates,
    
    // Tools for AI
    setLowerRateTool,
    setUpperRateTool,
    negotiateRateTool,
    callBrokerTool,
    emailBrokerTool,
    backToResultsTool,
  }
}